import { CascadiaColor, CascadiaMiniIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { Key, useState } from "react";
import {
  Header as AriaHeader,
  MenuTrigger as AriaMenuTrigger,
  Section as AriaSection,
  PopoverProps,
} from "react-aria-components";
import { MenuTriggerProps } from "react-stately";

import { Menu } from "./assets/Menu";
import { Separator } from "./assets/Separator";
import { Text } from "../../foundations/typography/Text";
import { _Popover } from "../_assets/_Popover";
import { ActionListItem } from "../action-list/ActionListItem";
import { GhostButton } from "../button/GhostButton";

export interface DropdownMenuProps extends MenuTriggerProps {
  triggerButton?: React.ReactElement;
  buttonIcon?: CascadiaMiniIcon | undefined;
  buttonBorderColor?: CascadiaColor;
  children: React.ReactElement | React.ReactElement[] | React.ReactNode;
  label?: string;
  headerTitle?: string;
  headerText?: string;
  hideSeparator?: boolean;
  id?: string;
  testID?: string;
  hideRightIcon?: boolean;
  onItemAction?(key: Key): void;
  offset?: PopoverProps["offset"];
  crossOffset?: PopoverProps["crossOffset"];
  placement?: PopoverProps["placement"];
  minWidth?: string;
  disabledKeys?: Iterable<Key> | undefined;
}

export function DropdownMenu(props: DropdownMenuProps) {
  const [isExpanded, setIsExpanded] = useState(props.defaultOpen || false);
  const {
    label,
    hideRightIcon,
    buttonIcon = isExpanded ? "expand_less" : "expand_more",
    buttonBorderColor,
    headerTitle,
    headerText,
    hideSeparator,
    children,
    minWidth = 184,
    testID = "dropdown-menu",
    offset,
    crossOffset,
    placement,
    triggerButton = (
      <GhostButton
        size="medium"
        aria-label="AriaMenu"
        border={buttonBorderColor}
        rightIcon={hideRightIcon ? undefined : buttonIcon}
        testID={`${testID}-trigger`}
      >
        <Text color="text-weak">{label}</Text>
      </GhostButton>
    ),
    disabledKeys,
    ...menuTriggerProps
  } = props;

  return (
    <AriaMenuTrigger
      isOpen={isExpanded}
      onOpenChange={setIsExpanded}
      {...menuTriggerProps}
    >
      {triggerButton}
      <_Popover
        className={css(styles.popover, {
          minWidth,
        })}
        offset={offset}
        crossOffset={crossOffset}
        placement={placement}
        testID={`${testID}-popover`}
      >
        <Menu {...props} disabledKeys={disabledKeys}>
          <>
            {headerTitle && (
              <AriaSection>
                <AriaHeader>
                  <ActionListItem title={headerTitle} children={headerText} />
                </AriaHeader>
              </AriaSection>
            )}
            {headerTitle && !hideSeparator && <Separator />}
            {children}
          </>
        </Menu>
      </_Popover>
    </AriaMenuTrigger>
  );
}

const styles = {
  popover: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    outline: "none",
  }),
};
